/**
 * Breadcrumb - 경로 탐색 (브레드크럼) 컴포넌트
 *
 * @param items - 경로 항목 배열 (label, href, icon)
 * @param separator - 구분자 ('chevron' | 'slash' | 'dot')
 */
'use client';

import React from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import styles from './Breadcrumb.module.css';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  separator?: 'chevron' | 'slash' | 'dot';
  variant?: 'text' | 'pill';
  className?: string;
}

function SeparatorIcon({ type }: { type: 'chevron' | 'slash' | 'dot' }) {
  if (type === 'slash')
    return (
      <span className={styles.separator} aria-hidden="true">
        /
      </span>
    );
  if (type === 'dot') return <span className={styles.separatorDot} aria-hidden="true" />;
  return (
    <span className={styles.separator} aria-hidden="true">
      <ChevronRight size={12} />
    </span>
  );
}

export default function Breadcrumb({
  items,
  separator = 'chevron',
  variant = 'text',
  className = '',
}: BreadcrumbProps) {
  return (
    <nav
      aria-label="breadcrumb"
      className={[styles.breadcrumb, className].filter(Boolean).join(' ')}
    >
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li key={index} className={styles.item}>
              {index > 0 && <SeparatorIcon type={separator} />}
              {isLast ? (
                <span
                  className={`${styles.current} ${variant === 'pill' ? styles.pill : ''}`}
                  aria-current="page"
                >
                  {item.icon && <span className={styles.icon}>{item.icon}</span>}
                  {item.label}
                </span>
              ) : item.href ? (
                <Link href={item.href} className={styles.link}>
                  {item.icon && <span className={styles.icon}>{item.icon}</span>}
                  {item.label}
                </Link>
              ) : (
                <span className={styles.text}>
                  {item.icon && <span className={styles.icon}>{item.icon}</span>}
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
